import dayjs from "dayjs";

const getComboUnitPriceFromBreakdown = (combo, productId) => {
  const breakdown = combo?.combo_details?.product_price_breakdown;
  if (!breakdown) return null;

  const foodId = combo.food_ids?.[0];
  const beverageId = combo.beverage_ids?.[0];
  if (!foodId || !beverageId) return null;

  const key1 = `${foodId}X${beverageId}`;
  const key2 = `${beverageId}X${foodId}`;

  const priceList = breakdown[key1] || breakdown[key2];
  if (!Array.isArray(priceList)) return null;

  const productPrice = priceList.find(
    (p) => Number(p.product_id) === Number(productId),
  );

  return Number(productPrice?.selling_price || 0);
};

const getSelectedComboProducts = (combo) => {
  // ✅ FIRST priority: UI selection
  const foodId = combo.food_ids?.[0];
  const beverageId = combo.beverage_ids?.[0];

  if (foodId || beverageId) {
    return [foodId, beverageId].filter(Boolean);
  }

  // ✅ fallback only for edit mode
  if (Array.isArray(combo._combo_items) && combo._combo_items.length) {
    return combo._combo_items.map((i) => i.product_id);
  }

  return [];
};

export const prepareOrdersPayloadWeb = ({
  customerOrders = [],
  internalOrders = [],
  comboOrders = [],
  journeyId,
  journeyLegId,
}) => {
  try {
    const ordersMap = {};
    const orderUuids = [];

    /* ==================================================
       1️⃣ EXPAND COMBO ORDERS → ROW LEVEL
    ================================================== */
    const expandedComboRows = comboOrders.flatMap((combo, index) => {
      const order = combo?.journey_order_id;
      if (!order || !combo.quantity) {
        return [];
      }

      const comboPaymentReason = order?.payment_exception_reasons || null;

      const uuid = order.unique_order_uuid || crypto.randomUUID();
      const comboSaleId = uuid;

      const resolvedComboId =
        combo.journey_order_id?.combo_id ?? // ✅ NEW selection FIRST
        combo._combo_items?.[0]?.combo_id ?? // fallback only
        combo.combo_id ??
        null;

      const comboName =
        combo?.combo_details?.combo_name || combo?.combo_name || "combo";

      const baseRow = {
        journey_order_id: {
          ...order,
          unique_order_uuid: uuid,
        },
        quantity: Number(combo.quantity),
        combo_sale_id: comboSaleId,
        combo_id: resolvedComboId,
        sale_type: comboName,
        payment_exception_reasons: comboPaymentReason || null,
        _isComboRow: true,
      };

      const rows = [];

      const selectedProducts = getSelectedComboProducts(combo);

      selectedProducts.forEach((productId, idx) => {
        const oldItem = combo._combo_items?.[idx];

        rows.push({
          ...baseRow,
          product_id: { id: productId }, // ✅ ALWAYS UI PRODUCT
          unit_price:
            getComboUnitPriceFromBreakdown(combo, productId) ??
            oldItem?.unit_price ??
            0,
          journey_order_item_id: oldItem?.journey_order_item_id || null,
        });
      });
      return rows;
    });

    /* ==================================================
       2️⃣ BUILD DISCOUNT MAP
    ================================================== */
    // const discountMap = {};
    // for (const row of customerOrders) {
    //   if (!row?.payment_exception_reasons) continue;
    //   const key = `${journeyLegId}_${row.journey_order_id?.seat}_${row.product_id?.id}`;
    //   discountMap[key] = Number(row.quantity || 0);
    // }

    /* ==================================================
       3️⃣ PROCESS ALL ROWS
    ================================================== */
    const allRows = [
      ...customerOrders,
      ...internalOrders,
      ...expandedComboRows,
    ];

    for (const [index, row] of allRows.entries()) {
      const order = row?.journey_order_id;
      if (!order) continue;

      const uuid = order.unique_order_uuid || crypto.randomUUID();

      /* ---------- INIT ORDER ---------- */
      if (!ordersMap[uuid]) {
        orderUuids.push(uuid);

        ordersMap[uuid] = {
          ...(order?.id ? { id: order.id } : {}),
          journey_id: Number(journeyId),
          journey_leg_id: journeyLegId,
          purchase_date: order.purchase_date || dayjs().format("YYYY-MM-DD"),
          unique_order_uuid: uuid,
          seat: order.seat || "",
          sold_by: order.sold_by || "SCC",
          internal_sales: Boolean(order.internal_sales),
          internal_user_id: order.internal_user_id || null,
          invoice_pnr: order.invoice_pnr || "",
          invoice_email: order.invoice_email || "",
          invoice_mobile: order.invoice_mobile || "",
          journey_order_item: [],
        };
      }

      const items = ordersMap[uuid].journey_order_item;

      /* ==================================================
         🔴 COMBO ROWS (UPDATE OR INSERT)
      ================================================== */
      if (row._isComboRow) {
        const existingCombo = items.find(
          (i) =>
            i.id === row.journey_order_item_id ||
            (i.product_id === row.product_id.id &&
              i.combo_sale_id === row.combo_sale_id &&
              i.sale_type === "combo"),
        );

        if (existingCombo) {
          existingCombo.quantity = row.quantity;
        } else {
          items.push({
            ...(row.journey_order_item_id
              ? { id: row.journey_order_item_id } // 🔑 THIS PREVENTS DUPLICATE INSERT
              : {}),
            product_id: row.product_id.id,
            quantity: row.quantity,
            unit_price: row.unit_price,
            mrp: row.unit_price,
            combo_id: row.combo_id,
            combo_sale_id: row.combo_sale_id,
            product_category: null,
            payment_exception_reasons: row.payment_exception_reasons,
            sale_type: row.sale_type,
          });
        }
        continue;
      }

      /* ==================================================
   NORMAL / DISCOUNT FLOW (FINAL FIX)
================================================== */

      const totalQty = Number(row.quantity || 0);
      if (!totalQty) continue;

      const productId = row.product_id?.id;
      if (!productId) continue;

      // const isDiscountApplied = row.is_discount_applied === true;

      const mrpPrice = Number(row.product_id?.selling_price || 0);
      // const discountPrice =
      //   row.product_id?.discount_price != null
      //     ? Number(row.product_id.discount_price)
      //     : mrpPrice;

      /* ✅ CASE 1: DISCOUNT SALE */
      // if (isDiscountApplied) {
      //   items.push({
      //     ...(row.id ? { id: row.id } : {}),
      //     product_id: productId,
      //     quantity: totalQty,

      //     mrp: mrpPrice, // ✅ ALWAYS ORIGINAL PRICE
      //     unit_price: discountPrice, // ✅ DISCOUNTED PRICE

      //     sale_type: "discount",
      //     payment_exception_reasons: row.payment_exception_reasons || null,
      //   });
      //   continue;
      // }

      /* ✅ CASE 2: NORMAL SALE */
      items.push({
        ...(row.id ? { id: row.id } : {}),
        product_id: productId,
        quantity: totalQty,

        mrp: mrpPrice, // ✅ ALWAYS ORIGINAL PRICE
        unit_price: mrpPrice, // ✅ SAME AS MRP
        sale_type: "individual",
        payment_exception_reasons: row.payment_exception_reasons || null,
      });
    }

    /* ==================================================
       4️⃣ FINAL PAYLOAD
    ================================================== */
    const payload = Object.values(ordersMap)
      .filter((o) => o.journey_order_item.length > 0)
      .map((order) => ({ journey_order: order }));

    return { orderUuids, payload };
  } catch (fatalError) {
    console.error("🔥 FATAL ERROR in prepareOrdersPayloadWeb", fatalError);
    return { orderUuids: [], payload: [], error: fatalError };
  }
};